import React, { useState, useEffect, useMemo } from "react";
import {
  Search, Filter, X, AlertTriangle,
  Users, Ban, Clock, Settings, Siren,
  FileText, Calendar, DollarSign, ShoppingBag,
  Phone, Mail, MapPin, User, Star, MessageSquare,
  Download, Trash2, Edit, Eye, CheckCircle, AlertOctagon,
  Timer, ArrowRight, Bell, Zap, CheckCircle2, UserCheck,
  Map, PhoneCall, HelpCircle, Wrench
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { useSelector } from "react-redux";
import { getAllJobs, useAppDispatch } from "../../redux";
import { getEmergencyProfessionals } from "../../redux/Actions/AdminAction";
import { GlobalService } from "../../modules";
import { getAllServiceAreas } from "../../redux/Actions/ServiceAreaAction";
import EditJobModal from "./EditJobModal";
import RequestDetailsModal from "./RequestDetailsModal";
import EmergencyProfessionalsModal from "./EmergencyProfessionalsModal";


const EmergencyRequestsPage = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const { allJobs } = useSelector((state: any) => state.auth);
  const isLoading = useSelector((state: any) => state.loader.isLoading);

  // State management
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [serviceType, setServiceType] = useState("all");
  const [status, setStatus] = useState("all");
  const [showRequestDetails, setShowRequestDetails] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [filters, setFilters] = useState({
    severity: "all",
    responseTime: "all",
    locationArea: "all"
  });

  // State for Edit Job Modal
  const [editJobModalOpen, setEditJobModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);

  // State for Emergency Professionals Modal
  const [showProfessionalsModal, setShowProfessionalsModal] = useState(false);
  const [selectedRequestForProvider, setSelectedRequestForProvider] = useState(null);
  const [professionalsLoading, setProfessionalsLoading] = useState(false);
  const emergencyProfessionalsState = useSelector((state: any) => state.admin.emergencyProfessionals);
  const emergencyProfessionals = emergencyProfessionalsState?.data || [];
  const emergencyProfessionalsTotal = emergencyProfessionalsState?.totalCount || 0;
  const emergencyProfessionalsPage = emergencyProfessionalsState?.currentPage || 1;
  const emergencyProfessionalsTotalPages = emergencyProfessionalsState?.totalPages || 1;
  const [professionalsPage, setProfessionalsPage] = useState(1);
  const [professionalsLimit] = useState(10);
  const [professionalsSearch, setProfessionalsSearch] = useState("");

  const fetchServiceAreas = async () => {
    try {
      const result = await dispatch(getAllServiceAreas({ page: 1, limit: 10, isShowLoader: false }));
      if (getAllServiceAreas.fulfilled.match(result)) {
        setServiceAreas(result.payload.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch service areas:", error);
    }
  };

  const fetchEmergencyProfessionals = async (page = 1, keyword = "") => {
    try {
      setProfessionalsLoading(true);
      await dispatch(
        getEmergencyProfessionals({
          page,
          limit: professionalsLimit,
          keyword,
          sortBy: "createdAt",
          sortOrder: "desc",
          isShowLoader: false,
        })
      );
    } catch (err) {
      console.error("Failed to fetch emergency professionals:", err);
      GlobalService.showToast("error", "Error", "Failed to fetch emergency professionals");
    } finally {
      setProfessionalsLoading(false);
    }
  };

  const fetchEmergencyRequests = async () => {
    try {
      const filterParams: any = {
        deadlineType: [5], // Emergency requests
        isEmergency: true // Additional emergency filter
      };

      // Map service type to category type (home, professional, international)
      if (serviceType !== "all") {
        filterParams.type = [serviceType];
      }

      // Map status to backend status IDs
      if (status !== "all") {
        filterParams.status = [Number(status)];
      }

      // Map severity to emergency level
      if (filters.severity !== "all") {
        filterParams.severity = filters.severity;
      }

      // Map response time
      if (filters.responseTime !== "all") {
        filterParams.responseTime = filters.responseTime;
      }

      // Map location area to service area
      if (filters.locationArea !== "all") {
        filterParams.serviceArea = filters.locationArea;
      }

      // Add search query as keyword filter if 2+ characters
      if (searchQuery && searchQuery.length >= 2) {
        filterParams.keyword = searchQuery;
      }

      // Add sorting according to backend format
      const sortOptions = [];

      switch (sortBy) {
        case "priority":
          sortOptions.push({ orderBy: "createdAt", order: "DESC" });
          break;
        case "newest":
          sortOptions.push({ orderBy: "createdAt", order: "DESC" });
          break;
        case "oldest":
          sortOptions.push({ orderBy: "createdAt", order: "ASC" });
          break;
        default:
          sortOptions.push({ orderBy: "createdAt", order: "DESC" });
      }

      await dispatch(getAllJobs({
        page: currentPage,
        limit,
        sort: sortOptions,
        filters: filterParams
      }));
    } catch (error) {
      console.error("Failed to fetch emergency requests:", error);
    }
  };

  // Fetch emergency requests on component mount and when filters change
  useEffect(() => {
    fetchEmergencyRequests();
  }, [currentPage, serviceType, status, sortBy, filters]);

  // Fetch service areas on component mount
  useEffect(() => {
    fetchServiceAreas();
    fetchEmergencyProfessionals(professionalsPage, professionalsSearch);
  }, []);

  // Fetch emergency professionals when modal opens or page/search changes with debounce and length check
  useEffect(() => {
    if (!showProfessionalsModal) return;

    const shouldSearch = professionalsSearch.length === 0 || professionalsSearch.length >= 3;
    if (!shouldSearch) return;

    const timeoutId = setTimeout(() => {
      fetchEmergencyProfessionals(professionalsPage, professionalsSearch);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [showProfessionalsModal, professionalsPage, professionalsSearch]);

  // Transform jobs data to emergency requests format
  const transformJobsToEmergencyRequests = (jobs) => {
    if (!jobs || !Array.isArray(jobs)) return [];

    return jobs.map(job => {
      const requestedAt = new Date(job.createdAt);
      const now = new Date();
      const waitTimeMs = now.getTime() - requestedAt.getTime();
      const waitTimeMinutes = Math.floor(waitTimeMs / (1000 * 60));

      let waitTime;
      if (waitTimeMinutes < 60) {
        waitTime = `${waitTimeMinutes}m`;
      } else if (waitTimeMinutes < 1440) {
        waitTime = `${Math.floor(waitTimeMinutes / 60)}h`;
      } else {
        waitTime = `${Math.floor(waitTimeMinutes / 1440)}d`;
      }

      // Determine severity based on job properties
      let severity = "moderate";
      if (job.isEmergency) {
        severity = "critical";
      } else if (job.budget > 500) {
        severity = "high";
      }

      // Map backend status to display status
      let status = "unassigned";
      const jobStatus = Number(job.status?.id);

      switch (jobStatus) {
        case 1:
          status = "unassigned";
          break;
        case 2:
          status = job.provider ? "provider_assigned" : "en_route";
          break;
        case 3:
          status = "on_site";
          break;
        case 4:
          status = "cancelled";
          break;
        case 5:
          status = "inactive";
          break;
        case 6:
          status = "active";
          break;
        case 7:
          status = "approved";
          break;
        case 8:
          status = "flagged";
          break;
        case 9:
          status = "deleted";
          break;
        case 10:
          status = "contracted";
          break;
        default:
          status = "unassigned";
      }

      // Check if order is flagged (separate from status)
      const isFlagged = job.isFlagged || false;

      return {
        id: job.id,
        type: job.categoryId?.name?.toLowerCase() || "general",
        service: job.categoryId?.name || "Emergency Service",
        status,
        severity,
        responseTime: "IMMEDIATE",
        customer: {
          name: job.contactName || "Anonymous",
          email: job.contactEmail || "N/A",
          phone: job.contactPhone || "N/A",
          address: `${job.address}, ${job.city}, ${job.province}`,
          location: {
            coordinates: { lat: 0, lng: 0 }, // Would need to be added to job data
            area: job.city || "Unknown Area"
          }
        },
        description: job.description || job.title,
        requestedAt: job.createdAt,
        waitTime,
        estimatedArrival: job.provider ? "En route" : "Searching Provider",
        nearbyProviders: 3, // Would need to be calculated
        callAttempts: 1,
        priority: job.isEmergency ? "critical" : "high",
        hazardLevel: severity,
        isFlagged: isFlagged,
        provider: job.provider ? {
          name: job.provider.name || "Assigned Provider",
          phone: job.provider.phone || "N/A",
          rating: job.provider.rating || 0,
          currentLocation: job.provider.location || "Unknown",
          eta: "Calculating...",
          verified: job.provider.verified || false
        } : null,
        notes: [
          {
            time: job.createdAt,
            author: "System",
            text: "Emergency request received, initiating provider search"
          }
        ]
      };
    });
  };

  // Get transformed emergency requests from real data
  const emergencyRequests = useMemo(() => {
    return transformJobsToEmergencyRequests(allJobs.data);
  }, [allJobs.data]);

  // Calculate pagination
  const totalPages = allJobs.totalPages || 1;
  const totalCount = allJobs.totalCount || 0;

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Debounced search effect - trigger after 2 characters
  useEffect(() => {
    if (searchQuery.length >= 2 || searchQuery.length === 0) {
      const timeoutId = setTimeout(() => {
        fetchEmergencyRequests();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle edit job
  const handleEditJob = (job) => {
    setEditingJob(job);
    setEditJobModalOpen(true);
  };

  // Handle edit job success
  const handleEditJobSuccess = () => {
    fetchEmergencyRequests();
    setEditJobModalOpen(false);
    setEditingJob(null);
  };

  // Calculate stats
  const stats = useMemo(() => {
    const activeEmergencies = totalCount;
    const criticalCases = emergencyRequests.filter(request => request.severity === "critical").length;
    const avgResponseTime = emergencyRequests.length > 0
      ? Math.round(emergencyRequests.reduce((sum, request) => {
        const timeStr = request.waitTime;
        if (timeStr.includes("h")) return sum + parseInt(timeStr) * 60;
        if (timeStr.includes("d")) return sum + parseInt(timeStr) * 1440;
        return sum + parseInt(timeStr);
      }, 0) / emergencyRequests.length)
      : 0;
    const availableProviders = emergencyProfessionalsTotal; // Use actual count from API

    return {
      activeEmergencies,
      criticalCases,
      avgResponseTime,
      availableProviders
    };
  }, [emergencyRequests, totalCount, emergencyProfessionals]);

  // Handle assign provider button click
  const handleAssignProvider = (request) => {
    setSelectedRequestForProvider(request);
    setShowProfessionalsModal(true);
  };

  // Handle assignment success callback
  const handleAssignmentSuccess = () => {
    setShowProfessionalsModal(false);
    setSelectedRequestForProvider(null);
    // Refresh the emergency requests to show updated status
    fetchEmergencyRequests();
  };



  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emergency Requests</h1>
                <p className="text-gray-600 mt-1">Monitor and manage urgent service requests</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export Report
                </Button>
              </div>
            </div>

            {/* Emergency Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active Emergencies</p>
                    <p className="text-2xl font-semibold">{stats.activeEmergencies.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Current emergencies</p>
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Siren className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Avg Response Time</p>
                    <p className="text-2xl font-semibold">{stats.avgResponseTime}m</p>
                    <p className="text-xs text-gray-400">Average wait time</p>
                  </div>
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Timer className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Available Providers</p>
                    <p className="text-2xl font-semibold">{stats.availableProviders.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">Emergency professionals</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Critical Cases</p>
                    <p className="text-2xl font-semibold">{stats.criticalCases.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">High priority</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <AlertOctagon className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 w-full sm:w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search emergency requests (min 2 characters)..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
                  <select
                    value={serviceType}
                    onChange={(e) => setServiceType(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Services</option>
                    <option value="1">Home Service</option>
                    <option value="2">Professional Service</option>
                    <option value="3">International Service</option>
                  </select>

                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Status</option>
                    <option value="1">Pending</option>
                    <option value="2">In Progress</option>
                    <option value="3">Completed</option>
                    <option value="4">Cancelled</option>
                    <option value="6">Active</option>
                    <option value="7">Approved</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Severity
                      </label>
                      <select
                        value={filters.severity}
                        onChange={(e) => handleFilterChange("severity", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Levels</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="moderate">Moderate</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Response Time
                      </label>
                      <select
                        value={filters.responseTime}
                        onChange={(e) => handleFilterChange("responseTime", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Times</option>
                        <option value="immediate">Immediate</option>
                        <option value="15min">Within 15 min</option>
                        <option value="30min">Within 30 min</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Area
                      </label>
                      <select
                        value={filters.locationArea}
                        onChange={(e) => handleFilterChange("locationArea", e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Areas</option>
                        {serviceAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Emergency Requests Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Emergency Info
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Response Time
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2">Loading emergency requests...</span>
                          </div>
                        </td>
                      </tr>
                    ) : emergencyRequests.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                          No emergency requests found
                        </td>
                      </tr>
                    ) : (
                      emergencyRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center">
                                <Siren className={`h-5 w-5 ${request.severity === "critical" ? "text-red-500" :
                                    request.severity === "high" ? "text-orange-500" :
                                      "text-yellow-500"
                                  }`} />
                              </div>
                              <div className="ml-4">
                                <div className="font-medium text-gray-900">{request.id}</div>
                                <div className="text-sm text-gray-500">{request.service}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Timer className="h-4 w-4 text-gray-400 mr-2" />
                              <span className={`${request.waitTime.includes("5") ? "text-red-600 font-medium" :
                                  "text-gray-500"
                                }`}>
                                {request.waitTime}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{request.customer.name}</div>
                            <div className="text-sm text-gray-500">{request.customer.phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <Badge variant={undefined} className={
                                request.status === "unassigned" ? "bg-red-100 text-red-700" :
                                  request.status === "provider_assigned" ? "bg-yellow-100 text-yellow-700" :
                                    request.status === "en_route" ? "bg-blue-100 text-blue-700" :
                                      "bg-green-100 text-green-700"
                              }>
                                {request.status.replace("_", " ").toUpperCase()}
                              </Badge>
                              {request.isFlagged && (
                                <Badge variant="outline" className="border-orange-200 text-orange-700 text-xs">
                                  FLAGGED
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500">
                              <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                              {request.customer.location.area}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowRequestDetails(request)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View Emergency
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing page {currentPage} of {totalPages}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Request Details Modal */}
      <RequestDetailsModal
        request={showRequestDetails}
        isOpen={!!showRequestDetails}
        onClose={() => setShowRequestDetails(null)}
        emergencyProfessionals={emergencyProfessionals}
        onAssignProvider={handleAssignProvider}
        onEditJob={handleEditJob}
      />

      {/* Edit Job Modal */}
      <EditJobModal
        isOpen={editJobModalOpen}
        onClose={() => setEditJobModalOpen(false)}
        job={editingJob}
        onSuccess={handleEditJobSuccess}
      />

      {/* Emergency Professionals Modal */}
      <EmergencyProfessionalsModal
        isOpen={showProfessionalsModal}
        onClose={() => setShowProfessionalsModal(false)}
        request={selectedRequestForProvider}
        emergencyProfessionals={emergencyProfessionals}
        professionalsLoading={professionalsLoading}
        emergencyProfessionalsTotal={emergencyProfessionalsTotal}
        emergencyProfessionalsPage={emergencyProfessionalsPage}
        emergencyProfessionalsTotalPages={emergencyProfessionalsTotalPages}
        professionalsPage={professionalsPage}
        setProfessionalsPage={setProfessionalsPage}
        onAssignmentSuccess={handleAssignmentSuccess}
        searchQuery={professionalsSearch}
        setSearchQuery={setProfessionalsSearch}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default EmergencyRequestsPage;